import { create } from 'zustand';
import { AppState, Team, Game, Player, Season } from '../types/game';
import { generateOpponentTeam, generateOpponentLineup } from '../utils/opponentTeam';
import { logger } from '../utils/logger';
import { auth } from '../config/firebase';
import {
  createGame as firestoreCreateGame,
  getTeamGames,
  getTeam,
  updateTeam as firestoreUpdateTeam,
  updateGame as firestoreUpdateGame,
  deleteGame as firestoreDeleteGame,
  createSeason as firestoreCreateSeason,
  getTeamSeasons,
  subscribeToTeamGames,
  subscribeToTeamSeasons,
} from '../utils/firestore';
import type { Unsubscribe } from 'firebase/firestore';

interface AppStore extends AppState {
  // Firestore関連
  currentTeamId: string | null;
  setCurrentTeamId: (teamId: string | null) => Promise<void>;
  loadFromFirestore: () => Promise<void>;
  // リアルタイムリスナーのクリーンアップ関数
  gamesUnsubscribe: Unsubscribe | null;
  seasonsUnsubscribe: Unsubscribe | null;
  // アクション
  updateMyTeam: (updates: Partial<Team>) => void;
  createGame: (game: Omit<Game, 'id' | 'status'>) => Promise<void>;
  updateGameLineup: (gameId: string, myTeamLineup: import('../types/game').LineupPlayer[], opponentLineup: import('../types/game').LineupPlayer[], benchPlayers?: import('../types/game').Player[]) => Promise<void>;
  updateGame: (gameId: string, updates: Partial<Game>) => Promise<void>;
  getCurrentGame: () => Game | undefined;
  deleteGame: (gameId: string) => Promise<void>;
  setCurrentGame: (gameId: string | undefined) => void;
  // シーズン管理
  createSeason: (name: string) => Promise<void>;
  setCurrentSeason: (seasonId: string) => void;
  setActiveSeason: (seasonId: string) => void;
  updateSeason: (seasonId: string, updates: Partial<Season>) => void;
  deleteSeason: (seasonId: string) => void;
  getCurrentSeason: () => Season | undefined;
  getCurrentSeasonGames: () => Game[];
  // データ再取得（Pull to Refresh等）
  refreshData: () => Promise<void>;
}

// デフォルトチーム
const createDefaultMyTeam = (): Team => {
  return {
    id: 'my-team',
    name: 'マイチーム',
    players: [],
  };
};

const createInitialAppState = (): AppState => {
  const currentYear = new Date().getFullYear();
  const defaultSeason: Season = {
    id: 'season-' + Date.now(),
    name: `${currentYear}シーズン`,
    startDate: new Date().toISOString(),
    isActive: true,
    createdAt: new Date().toISOString(),
  };
  
  return {
    myTeam: createDefaultMyTeam(),
    games: [],
    currentGameId: undefined,
    seasons: [defaultSeason],
    currentSeasonId: defaultSeason.id,
  };
};

export const useAppStore = create<AppStore>((set, get) => ({
  ...createInitialAppState(),
  currentTeamId: null,
  gamesUnsubscribe: null,
  seasonsUnsubscribe: null,
  
  setCurrentTeamId: async (teamId: string | null) => {
    const state = get();
    
    // 既存のリスナーをクリーンアップ
    if (state.gamesUnsubscribe) {
      console.log('[AppStore] Unsubscribing from previous games listener');
      state.gamesUnsubscribe();
    }
    if (state.seasonsUnsubscribe) {
      console.log('[AppStore] Unsubscribing from previous seasons listener');
      state.seasonsUnsubscribe();
    }
    
    set({ 
      currentTeamId: teamId,
      gamesUnsubscribe: null,
      seasonsUnsubscribe: null,
    });
    
    if (teamId) {
      console.log('[AppStore] Setting up realtime listeners for team:', teamId);

      // チーム情報（players/略称/ロゴ）をFirestoreから復元
      try {
        const teamDoc = await getTeam(teamId);
        if (teamDoc) {
          set((s) => ({
            myTeam: {
              ...s.myTeam,
              // idは従来通り略称として使っている箇所があるため、abbreviationがあればそれを優先
              id: teamDoc.abbreviation || s.myTeam.id,
              name: teamDoc.name || s.myTeam.name,
              abbreviation: teamDoc.abbreviation || '',
              logoUrl: teamDoc.logoUrl || '',
              players: (teamDoc.players as any[]) || [],
              gameLabels: teamDoc.gameLabels || s.myTeam.gameLabels || [],
            },
          }));
          console.log('[AppStore] My team restored from Firestore:', {
            teamId,
            teamName: teamDoc.name,
            playerCount: (teamDoc.players as any[])?.length || 0,
            abbreviation: teamDoc.abbreviation || '(empty)',
            logoUrl: teamDoc.logoUrl ? 'present' : 'absent',
            gameLabelsCount: teamDoc.gameLabels?.length || 0,
          });
        }
      } catch (e) {
        console.error('[AppStore] Failed to load team doc:', e);
      }
      
      // リアルタイムリスナーを設定
      // まずシーズンを取得し、シーズンが確定してからゲームリスナーを設定する
      let gamesUnsubscribe: Unsubscribe | null = null;
      
      const setupGamesListener = () => {
        // 既存のゲームリスナーをクリーンアップ
        if (gamesUnsubscribe) {
          console.log('[AppStore] Cleaning up previous games listener');
          gamesUnsubscribe();
        }

        console.log('[AppStore] Setting up games listener (all seasons)');

        gamesUnsubscribe = subscribeToTeamGames(teamId, (firestoreGames) => {
          console.log('[AppStore] Received realtime games update:', firestoreGames.length);

          const normalizeGameStatus = (
            rawStatus: any,
            hasGameState: boolean,
            hasFinalScore: boolean
          ): Game['status'] => {
            // 後方互換: in_progress / in-progress / completed / scheduled
            if (rawStatus === 'in_progress') return 'in-progress';
            if (rawStatus === 'in-progress') return 'in-progress';
            if (rawStatus === 'completed') return 'completed';
            if (rawStatus === 'scheduled') return 'scheduled';

            // statusが欠けている古いdoc対策
            if (hasFinalScore) return 'completed';
            if (hasGameState) return 'in-progress';
            return 'scheduled';
          };
          
          // FirestoreGame を Game に変換
          const appGames: Game[] = firestoreGames.map(g => {
            // 既存のゲームデータを保持（labelなど）
            const existingGame = get().games.find(eg => eg.id === g.id);
            
            // 古いFirestoreドキュメント互換: myTeamLineupが無い場合は startingLineup から生成
            const fallbackMyTeamLineup = (!g.myTeamLineup || g.myTeamLineup.length === 0) && Array.isArray((g as any).startingLineup)
              ? ((g as any).startingLineup as any[]).map((player: any, index: number) => ({
                  player,
                  battingOrder: index + 1,
                  fieldPosition: index + 1,
                }))
              : undefined;

            const normalizedStatus = normalizeGameStatus(
              (g as any).status ?? existingGame?.status,
              !!(g as any).gameState || !!existingGame?.gameState,
              !!(g as any).finalScore || !!existingGame?.finalScore
            );
            
            // Firestoreから読み込んだデータを優先（リロード時も保持される）
            return {
              id: g.id,
              seasonId: g.seasonId,
              opponentTeam: {
                name: g.opponentName,
              },
              isHome: g.isHome,
              useDH: g.useDH,
              date: g.date,
              venue: g.venue || '',
              myTeamLineup: g.myTeamLineup || fallbackMyTeamLineup || existingGame?.myTeamLineup || [],
              opponentLineup: g.opponentLineup || existingGame?.opponentLineup || [],
              benchPlayers: g.benchPlayers || existingGame?.benchPlayers || [],
              status: normalizedStatus,
              // null を尊重（クリア時に existing の値を復活させない）
              gameState: (g as any).gameState ?? existingGame?.gameState,
              finalScore: (g as any).finalScore ?? existingGame?.finalScore,
              label: existingGame?.label,
            };
          });
          
          set({ games: appGames });
        });

        set({ gamesUnsubscribe });
      };

      // ゲームリスナーを即座に設定（シーズンフィルタなし、全ゲームを取得）
      setupGamesListener();

      const seasonsUnsubscribe = subscribeToTeamSeasons(teamId, (firestoreSeasons) => {
        console.log('[AppStore] Received realtime seasons update:', firestoreSeasons.length);

        // FirestoreSeason を Season に変換
        const appSeasons: Season[] = firestoreSeasons.map(s => ({
          id: s.id,
          name: s.name,
          startDate: s.startDate,
          endDate: s.endDate,
          isActive: false, // 必要に応じて設定
          createdAt: s.createdAt,
        }));

        // シーズンが0件の場合、UIが空にならないように既存の状態を維持
        if (appSeasons.length === 0) {
          console.warn('[AppStore] No seasons from Firestore; keeping existing seasons');
          return;
        }

        const currentState = get();
        const nextCurrentSeasonId =
          currentState.currentSeasonId && appSeasons.some((s) => s.id === currentState.currentSeasonId)
            ? currentState.currentSeasonId
            : appSeasons[0].id;

        set({ seasons: appSeasons, currentSeasonId: nextCurrentSeasonId });
      });

      set({
        seasonsUnsubscribe,
      });
    } else {
      // チームIDがnullの場合はデータをクリア
      set({ 
        games: [],
        seasons: createInitialAppState().seasons,
      });
    }
  },
  
  loadFromFirestore: async () => {
    // リアルタイムリスナーが設定されているため、このメソッドは不要
    // ただし、後方互換性のために残しておく
    console.log('[AppStore] loadFromFirestore called, but using realtime listeners instead');
  },
  
  updateMyTeam: (updates: Partial<Team>) => {
    const state = get();
    const currentTeam = state.myTeam;
    
    if (!currentTeam) {
      console.error('[AppStore] No myTeam to update');
      return;
    }

    const updatedTeam = { ...currentTeam, ...updates };
    set({ myTeam: updatedTeam });
    
    console.log('[AppStore] updateMyTeam called with:', {
      teamName: updates.name,
      abbreviation: updates.abbreviation,
      playerCount: updates.players?.length,
      logoUrl: updates.logoUrl ? 'present' : 'absent',
      gameLabels: updates.gameLabels?.length,
    });

    // Firestoreにも保存（リロードで消えないように）
    if (!state.currentTeamId) {
      console.warn('[AppStore] No currentTeamId, skipping Firestore save');
      return;
    }

    // 保存するフィールドを明示的に指定
    const firestoreUpdates: any = {};
    
    if (updates.name !== undefined) {
      firestoreUpdates.name = updates.name;
    }
    if (updates.abbreviation !== undefined) {
      firestoreUpdates.abbreviation = updates.abbreviation || '';
      console.log('[AppStore] Saving abbreviation to Firestore:', updates.abbreviation || '(empty)');
    }
    if (updates.logoUrl !== undefined) {
      firestoreUpdates.logoUrl = updates.logoUrl || '';
    }
    if (updates.players !== undefined) {
      firestoreUpdates.players = updates.players as any;
    }
    if (updates.gameLabels !== undefined) {
      firestoreUpdates.gameLabels = updates.gameLabels as any;
    }

    if (Object.keys(firestoreUpdates).length > 0) {
      firestoreUpdateTeam(state.currentTeamId, firestoreUpdates as any).catch((e) => {
        console.error('[AppStore] Failed to save team to Firestore:', e);
      });
      console.log('[AppStore] Team updated in Firestore:', firestoreUpdates);
    } else {
      console.log('[AppStore] No updates to save to Firestore');
    }
  },
  
  createGame: async (game: Omit<Game, 'id' | 'status'>) => {
    const state = get();
    const seasonId = game.seasonId || state.currentSeasonId || (state.seasons?.[0]?.id || '');

    // Firestoreにも保存（FirestoreのdocIDをアプリのgame.idとして使う）
    if (state.currentTeamId) {
      try {
        const currentUser = auth.currentUser;
        if (currentUser) {
          const firestoreId = await firestoreCreateGame(state.currentTeamId, {
            seasonId,
            opponentName: game.opponentTeam.name,
            isHome: game.isHome,
            useDH: game.useDH,
            date: game.date,
            venue: game.venue || '',
            startingLineup: [], // 後方互換性のため（スタメン確定後に設定）
            myTeamLineup: [],   // スタメン確定後に設定
            opponentLineup: game.opponentLineup || [],
            records: [],
            status: 'scheduled',
            createdBy: currentUser.uid,
            gameState: null,
            finalScore: null,
          } as any);

          const newGame: Game = {
            ...game,
            id: firestoreId,
            status: 'scheduled',
            seasonId,
            myTeamLineup: [],
            gameState: null,
            finalScore: null,
          };

          set(prevState => ({
            games: [...prevState.games, newGame],
            currentGameId: newGame.id,
          }));

          console.log('[AppStore] Game created in Firestore:', firestoreId);
          logger.debug('[AppStore] Game created', { gameId: firestoreId, seasonId });
          return;
        }
      } catch (error) {
        console.error('[AppStore] Failed to sync game to Firestore:', error);
      }
    }

    // Firestoreが使えない場合でもUIが壊れないように一時IDで作成（オフライン等）
    const fallbackId = `game-${Date.now()}`;
    const newGame: Game = {
      ...game,
      id: fallbackId,
      status: 'scheduled',
      seasonId,
      myTeamLineup: [],
      gameState: null,
      finalScore: null,
    };

    set(prevState => ({
      games: [...prevState.games, newGame],
      currentGameId: newGame.id,
    }));

    logger.debug('[AppStore] Game created (fallback local)', { gameId: newGame.id, seasonId });
  },
  
  updateGameLineup: async (gameId: string, myTeamLineup: import('../types/game').LineupPlayer[], opponentLineup: import('../types/game').LineupPlayer[], benchPlayers?: import('../types/game').Player[]) => {
    set(state => ({
      games: state.games.map(game =>
        game.id === gameId
          ? { ...game, myTeamLineup, opponentLineup, ...(benchPlayers !== undefined ? { benchPlayers } : {}) }
          : game
      ),
    }));
    const state = get();

    // Firestoreにも保存
    if (state.currentTeamId) {
      try {
        const updates: Record<string, unknown> = {
          myTeamLineup,
          opponentLineup,
        };
        if (benchPlayers !== undefined) {
          updates.benchPlayers = benchPlayers;
        }
        await firestoreUpdateGame(gameId, updates);
        console.log('[AppStore] Game lineup synced to Firestore:', gameId);
      } catch (error) {
        console.error('[AppStore] Failed to sync game lineup to Firestore:', error);
      }
    }

    logger.debug('[AppStore] Game lineup updated', { gameId });
  },
  
  updateGame: async (gameId: string, updates: Partial<Game>) => {
    const game = get().games.find(g => g.id === gameId);
    if (game) {
      console.log('[AppStore] updateGame called:', { gameId, status: updates.status, finalScore: updates.finalScore });

      set(state => {
        const updatedGames = state.games.map(game =>
          game.id === gameId ? { ...game, ...updates } : game
        );
        
        // 試合が completed になったら currentGameId をクリア
        const newCurrentGameId = updates.status === 'completed' ? undefined : state.currentGameId;
        
        return {
          games: updatedGames,
          currentGameId: newCurrentGameId,
        };
      });
      
      // Firestoreにも更新
      const state = get();
      if (state.currentTeamId) {
        try {
          const firestoreUpdates: any = {};
          if (updates.opponentTeam) {
            firestoreUpdates.opponentName = updates.opponentTeam.name;
          }
          if (updates.isHome !== undefined) firestoreUpdates.isHome = updates.isHome;
          if (updates.useDH !== undefined) firestoreUpdates.useDH = updates.useDH;
          if (updates.date) firestoreUpdates.date = updates.date;
          if (updates.venue !== undefined) firestoreUpdates.venue = updates.venue;
          // gameStateがある場合、そのまま保存（SavedGameState型）
          if (updates.gameState !== undefined) {
            firestoreUpdates.gameState = updates.gameState;
          }
          // finalScoreがある場合、そのまま保存
          if (updates.finalScore !== undefined) {
            firestoreUpdates.finalScore = updates.finalScore;
          }
          // statusがある場合、そのまま保存
          if (updates.status !== undefined) {
            firestoreUpdates.status = updates.status;
          }
          // myTeamLineupがある場合、そのまま保存
          if (updates.myTeamLineup !== undefined) {
            firestoreUpdates.myTeamLineup = updates.myTeamLineup;
          }
          // opponentLineupがある場合、そのまま保存
          if (updates.opponentLineup !== undefined) {
            firestoreUpdates.opponentLineup = updates.opponentLineup;
          }
          
          await firestoreUpdateGame(gameId, firestoreUpdates);
          console.log('[AppStore] Game update synced to Firestore:', gameId);
        } catch (error) {
          console.error('[AppStore] Failed to sync game update to Firestore:', error);
          // endGame等で確実に失敗扱いにするため、握りつぶさない
          throw error;
        }
      }
      
      logger.debug('[AppStore] Game updated', { gameId, updates });
    }
  },
  
  getCurrentGame: () => {
    const state = get();
    return state.games.find(game => game.id === state.currentGameId);
  },
  
  deleteGame: async (gameId: string) => {
    set(state => ({
      games: state.games.filter(game => game.id !== gameId),
      currentGameId: state.currentGameId === gameId ? undefined : state.currentGameId,
    }));
    
    // Firestoreからも削除
    const state = get();
    if (state.currentTeamId) {
      try {
        await firestoreDeleteGame(gameId);
        console.log('[AppStore] Game deletion synced to Firestore:', gameId);
      } catch (error) {
        console.error('[AppStore] Failed to sync game deletion to Firestore:', error);
      }
    }
    
    logger.debug('[AppStore] Game deleted', { gameId });
  },
  
  setCurrentGame: (gameId: string | undefined) => {
    set({ currentGameId: gameId });
    logger.debug('[AppStore] Current game set', { gameId });
  },

  // シーズン管理機能
  createSeason: async (name: string) => {
    const newSeason: Season = {
      id: 'season-' + Date.now(),
      name,
      startDate: new Date().toISOString(),
      isActive: false,
      createdAt: new Date().toISOString(),
    };
    
    set((state) => ({
      seasons: [...(state.seasons || []), newSeason],
    }));
    // Firestoreにも保存
    const state = get();
    if (state.currentTeamId) {
      try {
        await firestoreCreateSeason(state.currentTeamId, {
          name: newSeason.name,
          startDate: newSeason.startDate,
          endDate: newSeason.endDate || '',
        });
        
        console.log('[AppStore] Season synced to Firestore:', newSeason.id);
      } catch (error) {
        console.error('[AppStore] Failed to sync season to Firestore:', error);
      }
    }
    
    logger.debug('[AppStore] Season created', { seasonId: newSeason.id, name });
  },

  setCurrentSeason: (seasonId: string) => {
    const state = get();
    const prevSeasonId = state.currentSeasonId;
    
    // 同じシーズンなら何もしない
    if (prevSeasonId === seasonId) return;
    
    set({ currentSeasonId: seasonId });
    logger.debug('[AppStore] Current season set', { seasonId });
    
    // チームIDがあればゲームリスナーを再設定
    if (state.currentTeamId) {
      // 既存のゲームリスナーをクリーンアップ
      if (state.gamesUnsubscribe) {
        console.log('[AppStore] Unsubscribing from previous games listener (season change)');
        state.gamesUnsubscribe();
      }
      
      console.log('[AppStore] Re-subscribing to games for new season:', seasonId);
      
      // 新しいシーズンでリスナーを再設定
      const gamesUnsubscribe = subscribeToTeamGames(state.currentTeamId, (firestoreGames) => {
        console.log('[AppStore] Received realtime games update:', firestoreGames.length, 'for season:', seasonId);

        const normalizeGameStatus = (
          rawStatus: any,
          hasGameState: boolean,
          hasFinalScore: boolean
        ): Game['status'] => {
          if (rawStatus === 'in_progress') return 'in-progress';
          if (rawStatus === 'in-progress') return 'in-progress';
          if (rawStatus === 'completed') return 'completed';
          if (rawStatus === 'scheduled') return 'scheduled';
          if (hasFinalScore) return 'completed';
          if (hasGameState) return 'in-progress';
          return 'scheduled';
        };
        
        const appGames: Game[] = firestoreGames.map(g => {
          const existingGame = get().games.find(eg => eg.id === g.id);
          
          const fallbackMyTeamLineup = (!g.myTeamLineup || g.myTeamLineup.length === 0) && Array.isArray((g as any).startingLineup)
            ? ((g as any).startingLineup as any[]).map((player: any, index: number) => ({
                player,
                battingOrder: index + 1,
                fieldPosition: index + 1,
              }))
            : undefined;

          const normalizedStatus = normalizeGameStatus(
            (g as any).status ?? existingGame?.status,
            !!(g as any).gameState || !!existingGame?.gameState,
            !!(g as any).finalScore || !!existingGame?.finalScore
          );
          
          return {
            id: g.id,
            seasonId: g.seasonId,
            opponentTeam: { name: g.opponentName },
            isHome: g.isHome,
            useDH: g.useDH,
            date: g.date,
            venue: g.venue || '',
            myTeamLineup: g.myTeamLineup || fallbackMyTeamLineup || existingGame?.myTeamLineup || [],
            opponentLineup: g.opponentLineup || existingGame?.opponentLineup || [],
            benchPlayers: g.benchPlayers || existingGame?.benchPlayers || [],
            status: normalizedStatus,
            gameState: (g as any).gameState ?? existingGame?.gameState,
            finalScore: (g as any).finalScore ?? existingGame?.finalScore,
            label: existingGame?.label,
          };
        });
        
        set({ games: appGames });
      }, seasonId);
      
      set({ gamesUnsubscribe });
    }
  },

  setActiveSeason: (seasonId: string) => {
    set((state) => ({
      seasons: (state.seasons || []).map((season) => ({
        ...season,
        isActive: season.id === seasonId,
      })),
    }));
    logger.debug('[AppStore] Active season set', { seasonId });
  },

  updateSeason: (seasonId: string, updates: Partial<Season>) => {
    set((state) => ({
      seasons: (state.seasons || []).map((season) =>
        season.id === seasonId ? { ...season, ...updates } : season
      ),
    }));
    
    logger.debug('[AppStore] Season updated', { seasonId, updates });
  },

  deleteSeason: (seasonId: string) => {
    const state = get();
    const gamesInSeason = (state.games || []).filter((g) => g.seasonId === seasonId);
    
    if (gamesInSeason.length > 0) {
      const confirm = window.confirm(
        `このシーズンには${gamesInSeason.length}試合が記録されています。削除しますか？`
      );
      if (!confirm) return;
    }
    
    set((prevState) => ({
      seasons: (prevState.seasons || []).filter((season) => season.id !== seasonId),
      games: (prevState.games || []).filter((game) => game.seasonId !== seasonId),
    }));
    
    // 現在のシーズンが削除された場合は別のシーズンに切り替え
    const updatedState = get();
    const currentSeason = (updatedState.seasons || []).find((s) => s.id === updatedState.currentSeasonId);
    if (!currentSeason && updatedState.seasons && updatedState.seasons.length > 0) {
      const firstSeason = updatedState.seasons[0];
      set({ currentSeasonId: firstSeason.id });
    }
    
    logger.debug('[AppStore] Season deleted', { seasonId });
  },

  getCurrentSeason: (): Season | undefined => {
    const state = get();
    return (state.seasons || []).find((s) => s.id === state.currentSeasonId);
  },

  getCurrentSeasonGames: (): Game[] => {
    const state = get();
    if (!state.currentSeasonId) return state.games || [];
    const filtered = (state.games || []).filter((g) => g.seasonId === state.currentSeasonId);
    return filtered.length > 0 ? filtered : (state.games || []);
  },

  refreshData: async () => {
    const state = get();
    if (!state.currentTeamId) return;
    // リスナーを再設定してFirestoreから最新データを取得
    await state.setCurrentTeamId(state.currentTeamId);
  },
}));

/**
 * アプリを初期化（ページロード時に呼び出す）
 */
export const initializeApp = (): void => {
  // Firestoreを唯一の真実の源にするため、localStorageからAppStateを復元しない
  logger.debug('[App] Initializing app state (no localStorage restore)');
  useAppStore.setState(createInitialAppState());
};


